import React, { useContext, useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  Icons,
  Tooltip,
  Typography,
  styled,
} from '@nextiva/next-ui-components';
import { useQueryClient } from 'react-query';
import { SelectSearchInput } from 'components/SelectSearchInput';
import { SectionSaveCancel } from 'components/SectionSaveCancel';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { useSnackbar, useTeamUpdateSMS } from 'hooks';
import {
  getPhoneNumber,
  getTeamPreferences,
  HuntGroupService,
  updateTeamPreferences,
} from 'services';
import { formatToPhoneNumber } from 'utils/phone-number';
import { pxToRem } from 'utils/pxToRemConverter';
import { Hint } from 'components/PhoneNumberHint';
import TeamSMSToggle from './TeamSMSToggle';

const Header = styled(Typography)`
  font-weight: 700;
  font-size: ${pxToRem('20px')};
  line-height: 28px;
  letter-spacing: 0.15px;
  padding-bottom: 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.secondary.main};
`;

const AdditionalHelp = styled(Icon)`
  width: 16px;
  height: 16px;
  margin: auto;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.blueGrey.A400};
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiTypography-body1 {
    width: 34vw;
    margin-left: 12px;
  }
`;

const StyledBox = styled(Box)`
  .MuiInputBase-root {
    color: ${({ theme }) => theme.palette.blueGrey[900]};
    border: 1px solid ${({ theme }) => theme.palette.blueGrey.A200};
    display: inline-flex;
    position: relative;
    font-size: 1rem;
    box-sizing: border-box;
    align-items: center;
    font-family: Lato, sans-serif;
    font-weight: 400;
    line-height: 1.1876em;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
  }
`;

const StyledDivider = styled(Divider)`
  margin-top: 32px;
  width: 100%;
  height: 1px;
`;

const PREFIX_FIRSTNAME_LASTINITIAL = 'PREFIX_FIRSTNAME_LASTINITIAL';
const NONE = 'NONE';

const TeamsMessaging = ({ match }) => {
  const setSnackbar = useSnackbar();
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsError, setSMSError] = useState('');
  const [confirmSMS, setConfirmSMS] = useState(false);
  const [deletePrimary, setDeletePrimary] = useState(false);
  const [state, setState] = useState({ availableNumbers: [] });
  const [prepend, setPrepend] = useState('');
  const settingsRef = useRef({});
  const queryClient = useQueryClient();
  const { corpAcctNbr } = useContext(FeatureContext);

  const canSave =
    settingsRef.current.preferences &&
    (settingsRef.current.phoneNumber !== phoneNumber ||
      settingsRef.current.preferences.signaturePreference !== prepend);
  const { groupId: huntGroupId, team } = match.params;

  const {
    mutateAsync: updateHuntGroupPhoneNumber,
    isLoading,
  } = useTeamUpdateSMS();

  const changePrepend = (event) => {
    setPrepend(event.target.checked ? PREFIX_FIRSTNAME_LASTINITIAL : NONE);
  };

  const getInitialData = async () => {
    try {
      const [
        {
          availableNumbers = [],
          primaryPhoneNumber,
          primaryExtension,
          forwardToPhoneNumber,
          locationId,
          huntGroupAlternatePhone: { alternateNumberList = [] },
        },
        { data: preferences },
      ] = await Promise.all([
        HuntGroupService.getHuntGroupAvailableNumbers(huntGroupId),
        getTeamPreferences(team),
      ]);

      setPrepend(preferences.signaturePreference);
      settingsRef.current.preferences = preferences;

      settingsRef.current.phoneNumber = primaryPhoneNumber;
      setPhoneNumber(primaryPhoneNumber);
      setState({
        locationId,
        primaryExtension,
        forwardToPhoneNumber,
        alternateNumberList,
        availableNumbers: availableNumbers.map((num) => ({
          label: formatToPhoneNumber(num),
          value: num,
        })),
      });

      if (primaryPhoneNumber) {
        getPhoneNumber(corpAcctNbr, primaryPhoneNumber).then((response) => {
          setSmsEnabled(response.data?.metadata.smsEnabled);
        });
      }
    } catch (err) {
      setSnackbar({
        message:
          'There was an error while retrieving phone number information for this team. Please try again later or contact support if the issue persists.',
        severity: 'error',
      });
    }
  };

  const onChange = (event) => {
    setPhoneNumber(event);
    setSMSError('');
  };

  const cancelChanges = () => {
    setPhoneNumber(settingsRef.current.phoneNumber);
    setPrepend(settingsRef.current.preferences.signaturePreference);
  };

  const updatePrepend = async () => {
    if (settingsRef.current.preferences.signaturePreference !== prepend) {
      const response = await updateTeamPreferences(team, {
        ...settingsRef.current.preferences,
        signaturePreference: prepend,
      });
      settingsRef.current.preferences = response.data;
      setState({ ...state });
    }
  };

  const cancelChange = () => {
    setConfirmSMS(false);
    setPhoneNumber(settingsRef.current.phoneNumber);
  };

  const cancelDelete = () => {
    setDeletePrimary(false);
    setPhoneNumber(settingsRef.current.phoneNumber);
  };

  const proceedToSave = async () => {
    const payload = {
      primaryExtension: state.primaryExtension,
      forwardToPhoneNumber: state.forwardToPhoneNumber,
      locationId: state.locationId,
      primaryPhoneNumber: phoneNumber,
      huntGroupAlternatePhone: {
        alternateNumberList: state.alternateNumberList,
      },
    };

    await updateHuntGroupPhoneNumber({
      huntGroupId,
      payload,
      locationId: state.locationId,
    });

    updatePrepend();

    settingsRef.current.phoneNumber = phoneNumber;

    setSnackbar({
      message: 'Success. Phone number settings have been saved.',
      severity: 'success',
    });
    if (!phoneNumber) {
      setSmsEnabled(false);
    }
    queryClient.invalidateQueries(['getTeams']);
    setConfirmSMS(false);
    setDeletePrimary(false);
  };

  const onSave = async () => {
    if (settingsRef.current.phoneNumber !== phoneNumber && smsEnabled) {
      if (phoneNumber) {
        setConfirmSMS(true);
      } else {
        setDeletePrimary(true);
      }
    } else {
      await proceedToSave();
    }
    updatePrepend();
  };

  useEffect(() => {
    if (!huntGroupId) return;
    getInitialData();
  }, [huntGroupId]);

  return (
    <>
      <StyledBox>
        <Grid container style={{ width: '636px' }}>
          <Grid item xs={12}>
            <Header
              variant="h1"
              data-testid="PlatformAdminUI-TeamMessaging-Heading"
            >
              Messaging
            </Header>
          </Grid>
          <TeamSMSToggle
            phoneNumber={phoneNumber}
            locationId={state.locationId}
            setSMSError={setSMSError}
            smsEnabled={smsEnabled}
            setSmsEnabled={setSmsEnabled}
            isLoading={isLoading}
            proceedToSave={proceedToSave}
            confirmSMS={confirmSMS}
            deletePrimary={deletePrimary}
            cancelChange={cancelChange}
            cancelDelete={cancelDelete}
            huntGroupId={huntGroupId}
          />
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              style={{ marginTop: '24px', marginBottom: '16px' }}
            >
              Primary Number
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Hint
              showRed={!!smsError}
              label="PHONE NUMBER"
              tooltipText="Select a phone number that can be directly dialed to reach this team.
                    If the team does not have a unique phone number assigned
                    then simply enter a unique extension for this team."
            />
          </Grid>
          <SelectSearchInput
            displayOptions={[
              {
                label: formatToPhoneNumber(phoneNumber),
                value: phoneNumber,
              },
              ...state.availableNumbers,
            ]}
            field={{ value: phoneNumber }}
            error={smsError}
            onChange={onChange}
            data-testid="PlatformAdminUI-TeamsMessaging-Select"
          />
          {smsEnabled && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  style={{ marginTop: '24px', marginBottom: '16px' }}
                >
                  Additional settings
                </Typography>
              </Grid>
              <Grid item display="flex" flexDirection="row" xs={12}>
                <StyledFormControlLabel
                  control={
                    <Checkbox
                      checked={prepend === PREFIX_FIRSTNAME_LASTINITIAL}
                      onChange={changePrepend}
                      data-testid="PlatformAdminUI-TeamsMessaging-CheckBox"
                    />
                  }
                  label="Prepend first name and last initial of user when messages
                    are sent from this team"
                />
                <Tooltip
                  placement="bottom"
                  title="Allows you to see the name of the team member sending the text message."
                  data-testid="PlatformAdminUI-TeamsMessaging-Tooltip"
                >
                  <AdditionalHelp icon={Icons.helpSolid} />
                </Tooltip>
              </Grid>
            </>
          )}
          <StyledDivider />
        </Grid>
      </StyledBox>
      <SectionSaveCancel
        open={canSave}
        cancelOnClick={cancelChanges}
        saveOnClick={onSave}
      />
    </>
  );
};

export default withRouter(TeamsMessaging);
